import { Theme } from '@mui/material'
import { createTheme, ThemeOptions } from '@mui/material/styles'
import { plPL as plPLDatePickers } from '@mui/x-date-pickers'
import type {} from '@mui/lab/themeAugmentation'
import type {} from '@mui/x-date-pickers/themeAugmentation'

declare module '@mui/material/styles' {
  interface TypographyVariants {
    badge: React.CSSProperties
    menuLabel: React.CSSProperties
    menuItem: React.CSSProperties
    minorButton: React.CSSProperties
    textButton: React.CSSProperties
    inputHint: React.CSSProperties
    subtext: React.CSSProperties
    button2: React.CSSProperties
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    badge?: React.CSSProperties
    menuLabel?: React.CSSProperties
    menuItem?: React.CSSProperties
    minorButton?: React.CSSProperties
    textButton?: React.CSSProperties
    inputHint?: React.CSSProperties
    subtext?: React.CSSProperties
    button2?: React.CSSProperties
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    badge: true
    menuLabel: true
    menuItem: true
    minorButton: true
    textButton: true
    inputHint: true
    subtext: true
    button2: true
  }
}

const theme = createTheme()

const palette: Theme = createTheme(theme, {
  palette: {
    primary: {
      main: '#000000'
    },
    secondary: {
      main: '#5C0F8B'
    },
    error: {
      main: '#E02020'
    },
  }
})

const typography: ThemeOptions = {
  typography: {
    subtitle1: {
      fontFamily: 'Roboto',
      fontWeight: 500,
      color: 'rgba(102 102 102 / 0.5)',
      [theme.breakpoints.up('md')]: {
        lineHeight: '1.5rem',
        fontSize: '1.25rem',
      },
      [theme.breakpoints.down('md')]: {
        lineHeight: '1.25rem',
        fontSize: '1rem',
      },
      [theme.breakpoints.down('sm')]: {
        lineHeight: '1.25rem',
        fontSize: '1rem',
      }
    },
    body1: {
      fontFamily: 'Roboto',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '1.25rem',
      [theme.breakpoints.up('md')]: {
        '--lh': '1.25rem',
        fontSize: '1rem',
        lineHeight: '1.25rem'
      },
      [theme.breakpoints.down('md')]: {
        '--lh': '1.25rem',
        fontSize: '1rem',
        lineHeight: '1.25rem'
      },
      [theme.breakpoints.down('sm')]: {
        '--lh': '1.25rem',
        fontSize: '.875rem',
        lineHeight: '1.25rem'
      }
    },
    body2: {
      fontFamily: 'Roboto',
      fontWeight: 400,
      [theme.breakpoints.up('md')]: {
        fontSize: '.875rem',
        lineHeight: '1rem'
      },
      [theme.breakpoints.down('md')]: {
        fontSize: '.875rem',
        lineHeight: '1rem'
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '.75rem',
        lineHeight: '.875rem'
      }
    },
    h1: {
      fontFamily: 'Roboto',
      fontWeight: 700,
      [theme.breakpoints.up('md')]: {
        lineHeight: '2.375rem',
        fontSize: '2rem'
      },
      [theme.breakpoints.down('md')]: {
        lineHeight: '1.75rem',
        fontSize: '1.5rem'
      },
      [theme.breakpoints.down('sm')]: {
        lineHeight: '1.75rem',
        fontSize: '1.5rem'
      }
    },
    h2: {
      fontFamily: 'Roboto',
      fontWeight: 700,
      [theme.breakpoints.up('md')]: {
        lineHeight: '2.125rem',
        fontSize: '1.75rem'
      },
      [theme.breakpoints.down('md')]: {
        lineHeight: '1.5rem',
        fontSize: '1.5rem'
      },
      [theme.breakpoints.down('sm')]: {
        lineHeight: '1.5rem',
        fontSize: '1.25rem'
      }
    },
    h3: {
      fontFamily: 'Roboto',
      fontWeight: 700,
      [theme.breakpoints.up('md')]: {
        lineHeight: '1.75rem',
        fontSize: '1.5rem'
      },
      [theme.breakpoints.down('md')]: {
        lineHeight: '1.75rem',
        fontSize: '1.25rem'
      },
      [theme.breakpoints.down('sm')]: {
        lineHeight: '1.75rem',
        fontSize: '1.125rem'
      }
    },
    h4: {
      fontFamily: 'Roboto',
      fontWeight: 700,
      [theme.breakpoints.up('md')]: {
        lineHeight: '1.25rem',
        fontSize: '1.25rem'
      },
      [theme.breakpoints.down('md')]: {
        lineHeight: '1.25rem',
        fontSize: '1.25rem'
      },
      [theme.breakpoints.down('sm')]: {
        lineHeight: '1.125rem',
        fontSize: '1.125rem'
      }
    },
    h5: {
      fontFamily: 'Roboto',
      fontWeight: 700,
      [theme.breakpoints.up('md')]: {
        lineHeight: '1.125rem',
        fontSize: '1.125rem'
      },
      [theme.breakpoints.down('md')]: {
        lineHeight: '1.125rem',
        fontSize: '1.125rem'
      },
      [theme.breakpoints.down('sm')]: {
        lineHeight: '1.125rem',
        fontSize: '1.125rem'
      }
    },
    h6: {
      fontFamily: 'Roboto',
      fontWeight: 700,
      [theme.breakpoints.up('md')]: {
        lineHeight: '1.125rem',
        fontSize: '1.125rem'
      },
      [theme.breakpoints.down('md')]: {
        lineHeight: '1.125rem',
        fontSize: '1.125rem'
      },
      [theme.breakpoints.down('sm')]: {
        lineHeight: '1.125rem',
        fontSize: '1.125rem'
      }
    },
    badge: {
      fontFamily: 'Roboto',
      color: palette.palette.primary.main,
      fontWeight: 700,
      [theme.breakpoints.up('md')]: {
        fontSize: '.875rem',
        lineHeight: '1.25rem'
      },
      [theme.breakpoints.down('md')]: {
        fontSize: '.875rem',
        lineHeight: '1.25rem'
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '.875rem',
        lineHeight: '1.25rem'
      }
    },
    menuLabel: {
      fontFamily: 'Roboto',
      color: 'rgba(187 187 187 / 0.5)',
      fontWeight: 700,
      fontSize: '0.75rem'
    },
    subtext: {
      fontFamily: 'Roboto',
      color: 'rgba(187 187 187)',
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: '1rem'
    },
    menuItem: {
      fontFamily: 'Roboto',
      color: palette.palette.primary.main,
      fontWeight: 500,
      [theme.breakpoints.up('md')]: {
        fontSize: '1rem',
        lineHeight: '1.25rem'
      },
      [theme.breakpoints.down('md')]: {
        fontSize: '1rem',
        lineHeight: '1.25rem'
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '.875rem',
        lineHeight: '1.25rem'
      }
    },
    minorButton: {
      fontFamily: 'Roboto',
      color: palette.palette.primary.main,
      fontWeight: 500,
      fontSize: '.875rem'
    },
    button: {
      fontFamily: 'Roboto',
      color: 'white',
      fontWeight: 700,
      fontSize: '1rem'
    },
    textButton: {
      fontFamily: 'Roboto',
      color: palette.palette.secondary.main,
      fontWeight: 500,
      [theme.breakpoints.up('md')]: {
        fontSize: '1rem',
        lineHeight: '1.125rem'
      },
      [theme.breakpoints.down('md')]: {
        fontSize: '1rem',
        lineHeight: '1.125rem'
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '.875rem',
        lineHeight: '1.125rem'
      }
    },
    inputHint: {
      fontFamily: 'Roboto',
      color: '#888888',
      fontWeight: 400,
      fontSize: '1rem'
    },
    button2: {
      fontFamily: 'Roboto',
      fontWeight: 500,
      [theme.breakpoints.up('md')]: {
        fontSize: '.875rem',
        lineHeight: '1.25rem'
      },
      [theme.breakpoints.down('md')]: {
        fontSize: '.875rem',
        lineHeight: '1.25rem'
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '.875rem',
        lineHeight: '1.25rem'
      }
    }
  }
}

const components: ThemeOptions = {
  components: {
    MuiDivider: {
      styleOverrides: {
        root: {
          borderWidth: '0.0625rem',
          color: '#BCBCBC'
        }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontFamily: 'Roboto',
          textDecoration: 'none',
          fontWeight: 500,
          color: palette.palette.secondary.main
        }
      }
    },
    MuiLoadingButton: {
      styleOverrides: {
        loadingIndicator: () => ({
          color: 'white'
        }),
        root: {
          fontWeight: 700,
          '&.MuiLoadingButton-loading': {
            backgroundColor: palette.palette.primary.main
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          lineHeight: '1.125rem'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        contained: {
          color: '#ffffff',
          '&.Mui-disabled': {
            color: '#ffffff'
          },
          ':hover': {
            backgroundColor: '#000000'
          },
          [theme.breakpoints.up('md')]: {
            height: '2.75rem'
          },
          [theme.breakpoints.down('md')]: {
            height: '2.75rem'
          },
          [theme.breakpoints.down('sm')]: {
            height: '2.5rem'
          }
        },
        outlined: {
          borderWidth: '0.0625rem',
          borderColor: '#000000',
          borderRadius: '0.5rem',
          padding: '0.3125rem 1rem 0.3125rem 1rem',
          ':hover': {
            backgroundColor: 'transparent'
          },
          [theme.breakpoints.up('md')]: {
            height: '2.75rem'
          },
          [theme.breakpoints.down('md')]: {
            height: '2.75rem'
          },
          [theme.breakpoints.down('sm')]: {
            height: '2.5rem'
          }
        },
        text: {
          fontFamily: 'Roboto',
          color: palette.palette.secondary.main,
          fontWeight: 500,
          padding: 0,
          width: 'max-content',
          display: 'inline',
          minWidth: 0,
          lineHeight: '1.125rem',
          backgroundColor: 'transparent !important',
          ':hover': {
            backgroundColor: 'transparent !important',
          },
          [theme.breakpoints.up('md')]: {
            fontSize: '1rem',
            lineHeight: '1.125rem'
          },
          [theme.breakpoints.down('md')]: {
            fontSize: '1rem',
            lineHeight: '1.125rem'
          },
          [theme.breakpoints.down('sm')]: {
            fontSize: '.875rem',
            lineHeight: '1.125rem'
          }
        },
        root: {
          fontWeight: 700,
          textTransform: 'none',
          borderRadius: '0.5rem',
          '&.Mui-disabled': {
            backgroundColor: '#bbbbbb',
          },
          [theme.breakpoints.up('md')]: {
            fontSize: '1rem'
          },
          [theme.breakpoints.down('md')]: {
            fontSize: '1rem'
          },
          [theme.breakpoints.down('sm')]: {
            fontSize: '.875rem'
          }
        }
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#888888',
          lineHeight: '1.4375em !important',
          '&.Mui-focused': {
            fontWeight: 700,
            color: palette.palette.secondary.main,
            '&.Mui-error': {
              color: palette.palette.error.main
            },
          },
          '&.MuiFormLabel-filled': {
            '&.Mui-error': {
              color: palette.palette.error.main
            },
            '&.Mui-focused': {
              color: palette.palette.secondary.main,
            },
            '&.Mui-error.Mui-focused': {
              color: palette.palette.error.main
            }
          },
        },
        shrink: {
          fontWeight: 700
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: '1rem',
          '.MuiInputBase-multiline': {
            height: 'unset !important'
          },
          '.MuiInputBase-inputMultiline': {
            padding: '0rem !important',
          },
          '.MuiOutlinedInput-root': {
            borderRadius: '0.5rem',
            [theme.breakpoints.up('md')]: {
              height: '3.5rem'
            },
            [theme.breakpoints.down('md')]: {
              height: '3.5rem'
            },
            [theme.breakpoints.down('sm')]: {
              height: '3rem'
            },
            '&.Mui-error': {
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: palette.palette.error.main,
              },
            },
            '&.Mui-focused': {
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: palette.palette.secondary.main,
              },
              '&.Mui-error': {
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.palette.error.main,
                },
              }
            },
            ':hover': {
              '&.Mui-error': {
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.palette.error.main,
                },
              },
              '&.Mui-focused': {
                '&.Mui-error': {
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: palette.palette.error.main,
                  },
                },
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.palette.secondary.main,
                },
              },
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: '#bbbbbb',
              },
            },
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: '#bbbbbb',
            },
            '&.MuiInputBase-adornedStart': {
              '.MuiOutlinedInput-input': {
                paddingLeft: 0
              }
            },
            '.MuiOutlinedInput-input': {
              letterSpacing: 0.8,
              color: '#444444',
              [theme.breakpoints.up('md')]: {
                fontSize: '1rem',
                padding: '1.03125rem .875rem',
              },
              [theme.breakpoints.down('md')]: {
                fontSize: '1rem',
                padding: '1.03125rem .875rem',
              },
              [theme.breakpoints.down('sm')]: {
                fontSize: '.875rem',
                lineHeight: '.875rem !important',
                padding: '.875rem .875rem',
              },
              '&::placeholder': {
                color: '#888888',
                letterSpacing: 0.8,
                opacity: 1,
              }
            }
          }
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          '.MuiPaper-root': {
            padding: '0',
            borderRadius: '0.5rem',
            boxShadow: '0px 1px 3px rgba(0,0,0, 0.2), 0px 2px 1px -1px rgba(0,0,0, 0.12), 0px 1px 1px rgba(0,0,0, 0.14)'
          },
          '.MuiMenuItem-root': {
            padding: '1rem',
            display: 'flex',
            flexDirection: 'row',
            gap: '0.5rem'
          }
        },
        list: {
          padding: 0
        },
        paper: {
          boxShadow: 'none'
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          position: 'relative',
          backgroundColor: '#ffffff',
          boxShadow: '0 0.125rem 1rem 0.125rem rgba(0, 0, 0, 0.16)',
          display: 'flex',
          flexDirection: 'row',
          padding: '0 1rem 0 1rem',
          justifyContent: 'space-between',
          alignItems: 'center',
          '.MuiButton-root': {
            width: '18rem',
            height: '1.875rem'
          },
          '.MuiIconButton-root': {
            padding: 0,
            '.MuiSvgIcon-root': {
              [theme.breakpoints.up('md')]: {
                fontSize: '2.5rem'
              },
              [theme.breakpoints.down('md')]: {
                fontSize: '2.5rem'
              },
              [theme.breakpoints.down('sm')]: {
                fontSize: '2rem'
              }
            }
          },
          [theme.breakpoints.up('md')]: {
            height: '3.5rem'
          },
          [theme.breakpoints.down('md')]: {
            height: '3.5rem'
          },
          [theme.breakpoints.down('sm')]: {
            height: '3rem'
          }
        },
      }
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'secondary'
      },
      styleOverrides: {
        root: {
          '.MuiSvgIcon-root': {
            [theme.breakpoints.up('md')]: {
              height: '1.125rem',
              width: '1.125rem'
            },
            [theme.breakpoints.down('md')]: {
              height: '1.125rem',
              width: '1.125rem'
            },
            [theme.breakpoints.down('sm')]: {
              height: '1rem',
              width: '1rem'
            }
          }
        }
      }
    },
    MuiCircularProgress: {
      defaultProps: {
        disableShrink: true
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          '.MuiCheckbox-root': {
            paddingBlock: 0
          }
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          whiteSpace: 'nowrap',
          [theme.breakpoints.up('md')]: {
            top: 0,
          },
          [theme.breakpoints.down('md')]: {
            top: '3.5rem',
          },
          [theme.breakpoints.down('sm')]: {
            top: '3rem',
          },
          '.MuiBackdrop-root': {
            [theme.breakpoints.up('md')]: {
              top: 0,
            },
            [theme.breakpoints.down('md')]: {
              top: '3.5rem',
            },
            [theme.breakpoints.down('sm')]: {
              top: '3rem',
            }
          },
          '.MuiSvgIcon-root': {
            color: '#fff',
            [theme.breakpoints.up('md')]: {
              fontSize: '1.5rem',
            },
            [theme.breakpoints.down('md')]: {
              fontSize: '1.5rem',
            },
            [theme.breakpoints.down('sm')]: {
              fontSize: '1.25rem',
            }
          },
          '.MuiDivider-root': {
            backgroundColor: '#BCBCBC',
            borderBottomWidth: 0,
            borderTopWidth: '1px'
          },
          '.MuiListItem-root': {
            '.Mui-selected': {
              backgroundColor: 'rgba(255 255 255 / 0.3) !important'
            }
          },
          '.MuiListItemButton-root': {
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            gap: '1.25rem',
            color: 'white',
            borderRadius: '0.3125rem',
            [theme.breakpoints.up('md')]: {
              height: '3rem',
            },
            [theme.breakpoints.down('md')]: {
              height: '3rem',
            },
            [theme.breakpoints.down('sm')]: {
              height: '2.5rem',
            },
            '.MuiListItemIcon-root': {
              minWidth: 0,
              padding: 0,
              color: 'white',
            }
          },
          '.MuiButton-root': {
            backgroundColor: '#fff',
            color: '#000',
            width: '100%',
            margin: '2rem 1rem 0 1rem',
            '&:hover': {
              backgroundColor: '#fff',
            },
            '.MuiSvgIcon-root': {
              color: '#000'
            }
          }
        },
        paper: {
          marginTop: '3.5rem',
          backgroundColor: palette.palette.primary.main,
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '0.4rem'
          },
          '&::-webkit-scrollbar-track': {
            boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
            webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,.25)',
            borderRadius: '0.2rem'
          },
          [theme.breakpoints.up('md')]: {
            marginTop: '3.5rem',
            height: 'calc(100% - 3.5rem)'
          },
          [theme.breakpoints.down('md')]: {
            marginTop: '3.5rem',
            height: 'calc(100% - 3.5rem)'
          },
          [theme.breakpoints.down('sm')]: {
            marginTop: '3rem',
            height: 'calc(100% - 3rem)'
          }
        }
      }
    },
    MuiFab: {
      styleOverrides: {
        root: {
          backgroundColor: palette.palette.primary.main,
          color: '#fff',
          position: 'fixed',
          bottom: 16,
          right: 16,
          '&:hover': {
            backgroundColor: palette.palette.primary.main
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '.5rem'
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '1rem',
          ':last-child': {
            padding: '1rem'
          }
        }
      }
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: 0
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 0,
          '.MuiButton-root': {
            [theme.breakpoints.up('md')]: {
              height: '2.25rem'
            },
            [theme.breakpoints.down('md')]: {
              height: '2.25rem'
            },
            [theme.breakpoints.down('sm')]: {
              height: '2.25rem'
            }
          }
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          paddingInline: 0,
          paddingBlock: '1rem'
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          paddingInline: 0,
          paddingTop: 0
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          '.MuiPaper-root': {
            [theme.breakpoints.up('md')]: {
              padding: '2rem'
            },
            [theme.breakpoints.down('md')]: {
              padding: '1.5rem'
            },
            [theme.breakpoints.down('sm')]: {
              padding: '1rem'
            }
          }
        }
      }
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: 'unset'
        }
      }
    },
    // MuiDateRangePickerToolbar: {
    //   styleOverrides: {
    //     root: {
    //       '.MuiPickersToolbar-penIconButton': {
    //         display: 'none'
    //       }
    //     }
    //   }
    // },
    // MuiDateRangePickerDay: {
    //   styleOverrides: {
    //     root: {
    //       '.Mui-selected': {
    //         backgroundColor: `${palette.palette.primary.main} !important`
    //       }
    //     }
    //   }
    // },
    MuiTable: {
      styleOverrides: {
        root: {
          '.MuiTableHead-root': {
            '.MuiTableCell-root': {
              backgroundColor: palette.palette.primary.main,
              color: '#ffffff'
            }
          },
          '> .MuiTableRow-root': {
            '.MuiTableCell-root': {
              backgroundColor: '#ffffff',
              ':first-child': {
                backgroundColor: 'rgba(187, 187, 187, 0.3)'
              }
            } 
          }          
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '.MuiOutlinedInput-root': {
            borderRadius: '0.5rem',
            height: 'unset !important',
            [theme.breakpoints.up('md')]: {
              minHeight: '3.5rem',
              padding: '9px'
            },
            [theme.breakpoints.down('md')]: {
              minHeight: '3.5rem',
              padding: '9px'
            },
            [theme.breakpoints.down('sm')]: {
              minHeight: '3rem',
              padding: '6px'
            }
          },
          '.MuiInputBase-input': {
            padding: '0 !important',
            [theme.breakpoints.up('md')]: {
              paddingLeft: '.3125rem !important'
            },
            [theme.breakpoints.down('md')]: {
              paddingLeft: '.3125rem !important'
            },
            [theme.breakpoints.down('sm')]: {
              paddingLeft: '.5rem !important'
            }
          },
          '.MuiChip-root': {
            [theme.breakpoints.up('md')]: {
              height: '2rem'
            },
            [theme.breakpoints.down('md')]: {
              height: '2rem'
            },
            [theme.breakpoints.down('sm')]: {
              height: '1.75rem'
            }
          }
        }
      }
    },
    // MuiMultiInputDateRangeField: {
    //   styleOverrides: {
    //     root: {
    //       '.MuiInputBase-input': {
    //         [theme.breakpoints.up('md')]: {
    //           padding: '1.03125rem .875rem !important',
    //         },
    //         [theme.breakpoints.down('md')]: {
    //           padding: '1.03125rem .875rem !important',
    //         },
    //         [theme.breakpoints.down('sm')]: {
    //           padding: '.875rem .875rem !important'
    //         }
    //       }
    //     }
    //   }
    // }
  }
}

const MainTheme = createTheme(palette, typography, components, plPLDatePickers)
export default MainTheme
export const maxPageWidth = '64rem'
